import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faClock, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp, faInstagram } from '@fortawesome/free-brands-svg-icons';
import './AllEvents.css';

const IMAGE_BASE = `${process.env.REACT_APP_API_BASE_URL1}/assets/img/events/`;
const PAST_STEP = 6;

const whatsappUrl = (text) =>
  `https://api.whatsapp.com/send/?phone=${process.env.REACT_APP_MOBILE_NUMBER1}&text=${encodeURIComponent(
    text
  )}&type=phone_number&app_absent=0`;

const formatDate = (d) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
const formatTime = (d) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const hasTime = (d) => d.getHours() !== 0 || d.getMinutes() !== 0; // midnight usually means "no time set"

// One event card (used for upcoming and past events)
const EventCard = ({ event, upcoming }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const date = new Date(event.EventDate);
  const validDate = !Number.isNaN(date.getTime());

  return (
    <article className={`rmev-card ${upcoming ? 'is-upcoming' : ''}`}>
      <div className="rmev-media">
        {event.Image && !imgFailed ? (
          <img
            src={IMAGE_BASE + event.Image}
            alt={event.EventTitle}
            loading="lazy"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="rmev-media-empty" aria-hidden="true" />
        )}
        {event.EventPlaceName && <span className="rmev-tag">{event.EventPlaceName}</span>}
        {upcoming && <span className="rmev-soon">Upcoming</span>}
      </div>

      <div className="rmev-body">
        <h3 className="rmev-title">{event.EventTitle}</h3>

        <ul className="rmev-meta">
          {validDate && (
            <li>
              <FontAwesomeIcon icon={faCalendarAlt} />
              {formatDate(date)}
            </li>
          )}
          {validDate && upcoming && hasTime(date) && (
            <li>
              <FontAwesomeIcon icon={faClock} />
              {formatTime(date)}
            </li>
          )}
          {event.EventAddress && (
            <li>
              <FontAwesomeIcon icon={faLocationDot} />
              {event.EventAddress}
            </li>
          )}
        </ul>

        {event.EventDetails && <p className="rmev-text">{event.EventDetails}</p>}

        {upcoming && (
          <a
            href={whatsappUrl(`Hi! I'd like to join the event: ${event.EventTitle}`)}
            target="_blank"
            rel="noopener noreferrer"
            className="rmev-btn"
          >
            <FontAwesomeIcon icon={faWhatsapp} />
            Join this event
          </a>
        )}
      </div>
    </article>
  );
};

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [filter, setFilter] = useState('All');
  const [pastVisible, setPastVisible] = useState(PAST_STEP);

  useEffect(() => {
    let cancelled = false;
    const fetchEvents = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/alleventsection`);
        if (!response.ok) throw new Error(String(response.status));
        const data = await response.json();
        if (!cancelled) {
          setEvents(Array.isArray(data) ? data : []);
          setStatus('ready');
        }
      } catch (err) {
        if (!cancelled) setStatus('error');
      }
    };
    fetchEvents();
    return () => {
      cancelled = true;
    };
  }, []);

  const tags = ['All', ...new Set(events.map((e) => e.EventPlaceName).filter(Boolean))];
  const filtered = events.filter((e) => filter === 'All' || e.EventPlaceName === filter);

  // Split into upcoming (soonest first) and past (newest first)
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const time = (e) => new Date(e.EventDate).getTime() || 0;
  const upcoming = filtered.filter((e) => time(e) >= startOfToday.getTime()).sort((a, b) => time(a) - time(b));
  const past = filtered.filter((e) => !upcoming.includes(e)).sort((a, b) => time(b) - time(a));

  return (
    <section className="rmev" aria-labelledby="rmev-title">
      <div className="rmev-inner">
        <header className="rmev-header">
          <span className="rmev-eyebrow">Events &amp; workshops</span>
          <h1 id="rmev-title" className="rmev-title-page">
            Coding &amp; robotics, <span>in real life</span>
          </h1>
          <p className="rmev-sub">
            Workshops, school days and robotics events where kids build, code and compete together.
          </p>
        </header>

        {status === 'loading' && (
          <div className="rmev-grid" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rmev-card rmev-skeleton" />
            ))}
          </div>
        )}

        {status === 'error' && (
          <div className="rmev-empty">
            <h2>Events couldn’t load right now</h2>
            <p>Please try again in a moment, or message us to ask about upcoming workshops.</p>
            <a href={whatsappUrl("Hi! Do you have any upcoming events or workshops?")} target="_blank" rel="noopener noreferrer" className="rmev-btn">
              <FontAwesomeIcon icon={faWhatsapp} />
              Ask on WhatsApp
            </a>
          </div>
        )}

        {status === 'ready' && (
          <>
            {tags.length > 2 && (
              <div className="rmev-filters" role="group" aria-label="Filter events">
                {tags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    aria-pressed={filter === tag}
                    className={`rmev-filter ${filter === tag ? 'is-active' : ''}`}
                    onClick={() => {
                      setFilter(tag);
                      setPastVisible(PAST_STEP);
                    }}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}

            {/* Upcoming */}
            <div className="rmev-block">
              <h2 className="rmev-h2">Upcoming</h2>
              {upcoming.length > 0 ? (
                <div className="rmev-grid">
                  {upcoming.map((e, i) => (
                    <EventCard key={e.id ?? `${e.EventTitle}-${i}`} event={e} upcoming />
                  ))}
                </div>
              ) : (
                <div className="rmev-empty rmev-empty--inline">
                  <p>
                    No upcoming events right now. Follow us for the next one, or message us to organize a workshop for
                    your school, center or birthday.
                  </p>
                  <div className="rmev-empty-actions">
                    <a href="https://instagram.com/robomind.lb" target="_blank" rel="noopener noreferrer" className="rmev-btn rmev-btn--ghost">
                      <FontAwesomeIcon icon={faInstagram} />
                      @robomind.lb
                    </a>
                    <a href={whatsappUrl("Hi! I'd like to organize a robotics workshop.")} target="_blank" rel="noopener noreferrer" className="rmev-btn">
                      <FontAwesomeIcon icon={faWhatsapp} />
                      Organize a workshop
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Past */}
            {past.length > 0 && (
              <div className="rmev-block">
                <h2 className="rmev-h2">Past events</h2>
                <div className="rmev-grid">
                  {past.slice(0, pastVisible).map((e, i) => (
                    <EventCard key={e.id ?? `${e.EventTitle}-${i}`} event={e} />
                  ))}
                </div>
                {pastVisible < past.length && (
                  <button type="button" className="rmev-more" onClick={() => setPastVisible((v) => v + PAST_STEP)}>
                    Show more past events ({past.length - pastVisible})
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default EventsPage;